interface Step {
  key: string;
  label: string;
}

interface StepIndicatorProps {
  steps: Step[];
  /** Zero-based index of the active step. */
  current: number;
  /** Called when the user clicks a step indicator (0-based). */
  onJump?: (index: number) => void;
}

/**
 * Visual progress bar with numbered steps. Clicking a previously
 * visited step jumps back; clicking a forward step is a no-op (we don't
 * want users to skip past validation).
 */
export function StepIndicator({ steps, current, onJump }: StepIndicatorProps) {
  const total = steps.length;
  const pct = total > 0 ? Math.round(((current + 1) / total) * 100) : 0;

  return (
    <div className="w-full" aria-label={`Step ${current + 1} of ${total}`}>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>
          Step {current + 1} of {total}
        </span>
        <span className="font-mono tabular-nums">{pct}%</span>
      </div>
      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full bg-brand-600 transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      <ol className="mt-4 flex flex-wrap gap-2 text-xs">
        {steps.map((s, idx) => {
          const isActive = idx === current;
          const isComplete = idx < current;
          const isClickable = onJump !== undefined && idx <= current;
          const base =
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1 transition-colors';
          const tone = isActive
            ? 'bg-brand-600 text-white'
            : isComplete
            ? 'bg-brand-100 text-brand-700 hover:bg-brand-200'
            : 'bg-slate-100 text-slate-500';
          return (
            <li key={s.key}>
              <button
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onJump?.(idx)}
                className={`${base} ${tone} ${
                  isClickable ? 'cursor-pointer' : 'cursor-default'
                } disabled:cursor-default`}
                aria-current={isActive ? 'step' : undefined}
              >
                <span className="font-mono tabular-nums">{idx + 1}</span>
                <span>{s.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}